/**
 * PseudoModal — Modale de création/modification pseudo + avatar DiceBear
 * Challenges V2 (Lot 1)
 * Redimensionnable sur desktop via useResizable (même pattern que CreateTargetModal)
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, RefreshCw, Check, AlertCircle, X } from "lucide-react";
import { pseudoApi } from "../lib/api";
import { useResizable } from "../hooks/useResizable";

const DICEBEAR_STYLE = "micah";
const AVATARS_PER_PAGE = 6;

/** Paramètres DiceBear micah personnalisés */
const DICEBEAR_PARAMS: Record<string, string> = {
  backgroundType: "solid",
  baseColor: "f9c9b6,f9c9b6,ac6651",
  earrings: "hoop",
  earringsProbability: "60",
  eyebrows: "up",
  eyebrowsColor: "000000",
  eyesColor: "000000",
  facialHairColor: "000000",
  facialHairProbability: "20",
  glassesProbability: "40",
  hair: "dannyPhantom,dougFunny,fonze,full,mrClean,pixie",
  mouth: "laughing,smile,smirk",
};

function getDicebearUrl(seed: string): string {
  const params = new URLSearchParams({ seed, ...DICEBEAR_PARAMS });
  return `https://api.dicebear.com/9.x/${DICEBEAR_STYLE}/svg?${params}`;
}

function generateSeeds(count: number): string[] {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: count }, () => {
    let seed = "av_";
    for (let i = 0; i < 8; i++) seed += chars[Math.floor(Math.random() * chars.length)];
    return seed;
  });
}

interface PseudoModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (pseudo: string, avatarSeed: string, avatarUrl: string) => void;
  currentPseudo?: string | null;
  currentAvatarSeed?: string | null;
}

export function PseudoModal({ open, onClose, onSaved, currentPseudo, currentAvatarSeed }: PseudoModalProps) {
  const { i18n } = useTranslation();
  const isFr = i18n.language === "fr";

  const { containerRef, onPointerDown, boxStyle } = useResizable({
    minWidth: 340,
    minHeight: 420,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 40 : 900,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 40 : 800,
    defaultWidth: 480,
    defaultHeight: 580,
  });

  const [pseudo, setPseudo] = useState(currentPseudo || "");
  const [selectedSeed, setSelectedSeed] = useState(currentAvatarSeed || "");
  const [seeds, setSeeds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Init seeds on open
  useEffect(() => {
    if (open) {
      const initial = generateSeeds(AVATARS_PER_PAGE);
      if (currentAvatarSeed) {
        setSeeds([currentAvatarSeed, ...initial.slice(0, AVATARS_PER_PAGE - 1)]);
        setSelectedSeed(currentAvatarSeed);
      } else {
        setSeeds(initial);
        setSelectedSeed(initial[0]);
      }
      setPseudo(currentPseudo || "");
      setError("");
      setSuggestions([]);
    }
  }, [open, currentPseudo, currentAvatarSeed]);

  const handleShowMore = () => {
    const more = generateSeeds(20);
    setSeeds((prev) => [...prev, ...more]);
  };

  const handleSubmit = async () => {
    const trimmed = pseudo.trim();
    if (!trimmed) {
      setError(isFr ? "Le pseudo est requis." : "Username is required.");
      return;
    }

    setLoading(true);
    setError("");
    setSuggestions([]);

    try {
      const result = await pseudoApi.setPseudo(trimmed, selectedSeed || undefined);
      onSaved(
        result.pseudo,
        result.avatarSeed,
        result.avatarUrl || getDicebearUrl(result.avatarSeed),
      );
    } catch (err: any) {
      setError(err.message || (isFr ? "Erreur lors de la sauvegarde." : "Error saving."));
      if (err.suggestions) setSuggestions(err.suggestions);
      if (err.suggestion) setSuggestions([err.suggestion]);
    } finally {
      setLoading(false);
    }
  };

  const handleUseSuggestion = (suggestion: string) => {
    setPseudo(suggestion);
    setSuggestions([]);
    setError("");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" onClick={() => { if (!document.body.style.cursor) onClose(); }}>
      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={boxStyle(480, 580)}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ backgroundColor: "#F6C847" + "20" }}>
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
              {currentPseudo
                ? (isFr ? "Modifier mon profil joueur" : "Edit my player profile")
                : (isFr ? "Créer mon profil joueur" : "Create my player profile")
              }
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isFr
                ? "Les autres joueurs ne verront que votre pseudo et avatar."
                : "Other players will only see your username and avatar."
              }
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          {/* Pseudo */}
          <div className="space-y-2">
            <Label htmlFor="pseudo-input">{isFr ? "Mon pseudo" : "My username"}</Label>
            <Input
              id="pseudo-input"
              value={pseudo}
              onChange={(e) => { setPseudo(e.target.value); setError(""); setSuggestions([]); }}
              placeholder={isFr ? "Ex: Champion42, ProSales, etc." : "E.g. Champion42, ProSales, etc."}
              maxLength={20}
              disabled={loading}
              aria-label={isFr ? "Pseudo" : "Username"}
            />
            <p className="text-xs text-muted-foreground">
              {isFr
                ? "3 à 20 caractères. Lettres, chiffres, tirets et underscores."
                : "3 to 20 characters. Letters, numbers, hyphens and underscores."
              }
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p>{error}</p>
                {suggestions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-500 mb-1">
                      {isFr ? "Suggestions disponibles :" : "Available suggestions:"}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="px-2 py-0.5 text-xs bg-white border border-red-200 rounded hover:bg-red-100 transition-colors"
                          onClick={() => handleUseSuggestion(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Avatar picker — grille scrollable */}
          <div className="space-y-2">
            <Label>{isFr ? "Mon avatar" : "My avatar"}</Label>
            <div className="grid grid-cols-4 gap-2">
              {seeds.map((seed) => (
                <button
                  key={seed}
                  type="button"
                  className={`p-1.5 rounded-xl border-2 transition-all hover:scale-105 ${
                    selectedSeed === seed
                      ? "border-[#F6C847] bg-yellow-50 shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedSeed(seed)}
                  aria-label={`Avatar ${seed}`}
                >
                  <img
                    src={getDicebearUrl(seed)}
                    alt={`Avatar ${seed}`}
                    className="w-full h-auto rounded-lg"
                    loading="lazy"
                  />
                  {selectedSeed === seed && (
                    <div className="flex justify-center mt-1">
                      <Check className="h-3 w-3 text-[#926B00]" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleShowMore}
              className="w-full"
              disabled={loading}
            >
              <RefreshCw className="h-3 w-3 mr-2" />
              {isFr ? "Afficher plus" : "Show more"}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50/50 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {isFr ? "Annuler" : "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !pseudo.trim()}
            style={{ backgroundColor: "#F6C847", color: "#926B00" }}
            className="hover:opacity-90"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {currentPseudo
              ? (isFr ? "Enregistrer" : "Save")
              : (isFr ? "Créer mon profil" : "Create my profile")
            }
          </Button>
        </div>

        {/* Resize handles (desktop only) */}
        <div className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize hidden sm:block" onPointerDown={onPointerDown("right")} />
        <div className="absolute bottom-0 left-0 w-full h-1.5 cursor-ns-resize hidden sm:block" onPointerDown={onPointerDown("bottom")} />
        <div className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize hidden sm:flex items-end justify-end p-0.5" onPointerDown={onPointerDown("corner")}>
          <svg width="10" height="10" viewBox="0 0 10 10" className="text-gray-400">
            <circle cx="8" cy="2" r="1" fill="currentColor" />
            <circle cx="8" cy="5.5" r="1" fill="currentColor" />
            <circle cx="8" cy="9" r="1" fill="currentColor" />
            <circle cx="4.5" cy="5.5" r="1" fill="currentColor" />
            <circle cx="4.5" cy="9" r="1" fill="currentColor" />
            <circle cx="1" cy="9" r="1" fill="currentColor" />
          </svg>
        </div>
      </div>
    </div>
  );
}
